import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { Paper, Typography, Button, Divider } from '@material-ui/core';
import { ExpandMore, Close } from '@material-ui/icons';
import { isValid } from 'date-fns';
import useIsFieldRequired from '../../../utility/useIsFieldRequired.js';
import { buildPresets } from '../../../constants/selectFilter.js';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { formatDateValue } from '../../../../shared/utility/dateFormatter.js';
import RMSChip from '../../chips/RMSChip/index.jsx';
import DATE_FORMATS from '../../../constants/dateFormats.js';

const RMSDateRangePicker = ({
  name,
  label = 'Select date range',
  disabled = false,
  presets,
  minDate,
  maxDate,
  onRangeChange,
  width,
  ...rest
}) => {
  const { control, trigger, setValue, watch } = useFormContext();
  const isRequired = useIsFieldRequired(name);

  const fromName = `${name}.from`;
  const toName = `${name}.to`;

  const fromVal = watch(fromName);
  const toVal = watch(toName);

  const [open, setOpen] = useState(false);

  const allPresets = presets || buildPresets();
  const isActive = !!(fromVal || toVal);

  const applyPreset = (preset) => {
    setValue(fromName, formatDateValue(preset.from), { shouldValidate: true });
    setValue(toName, formatDateValue(preset.to), { shouldValidate: true });
    trigger([fromName, toName]);
    if (onRangeChange) onRangeChange(formatDateValue(preset.from), formatDateValue(preset.to));
  };

  const clearRange = (e) => {
    e?.stopPropagation();
    setValue(fromName, null, { shouldValidate: true });
    setValue(toName, null, { shouldValidate: true });
    trigger([fromName, toName]);
    if (onRangeChange) onRangeChange(null, null);
  };

  const handleDateChange = (type, date, field) => {
    const fieldName = type === 'from' ? fromName : toName;

    if (!date) {
      field.onChange(null);
      trigger(fieldName);
      return;
    }

    const selected = formatDateValue(date);

    if (type === 'from') {
      field.onChange(selected);
      trigger(fromName);
      if (toVal && selected > formatDateValue(toVal)) {
        setValue(toName, null, { shouldValidate: true });
      }
      if (onRangeChange) onRangeChange(selected, toVal);
    } else {
      const fromDate = fromVal ? formatDateValue(fromVal) : null;
      if (fromDate && selected < fromDate) {
        field.onChange(null);
        trigger(toName);
        return;
      }
      field.onChange(selected);
      trigger(toName);
      if (onRangeChange) onRangeChange(fromVal, selected);
    }
  };

  const matchedPreset = allPresets.find(
    (p) =>
      fromVal &&
      toVal &&
      isValid(new Date(fromVal)) &&
      isValid(new Date(toVal)) &&
      formatDateValue(p.from) === fromVal &&
      formatDateValue(p.to) === toVal
  );

  const getTriggerLabel = () => {
    if (matchedPreset) return matchedPreset.label;
    if (!isActive) return label;
    if (fromVal && toVal)
      return `${formatDateValue(fromVal, DATE_FORMATS.MM_DD_YYYY)} – ${formatDateValue(
        toVal,
        DATE_FORMATS.MM_DD_YYYY
      )}`;
    if (fromVal) return `From ${formatDateValue(fromVal, DATE_FORMATS.MM_DD_YYYY)}`;
    return `To ${formatDateValue(toVal, DATE_FORMATS.MM_DD_YYYY)}`;
  };
  const triggerLabel = getTriggerLabel();

  const commonPickerProps = {
    ...rest,
    autoOk: true,
    fullWidth: true,
    disableToolbar: true,
    variant: 'inline',
    inputVariant: 'outlined',
    size: 'small',
    format: DATE_FORMATS.DD_MM_YYYY,
    placeholder: DATE_FORMATS.DD_MM_YYYY,
    views: ['year', 'month', 'date'],
    openTo: 'date',
    label: '',
    disabled,
    KeyboardButtonProps: { size: 'small' },
  };

  const renderPicker = (type) => {
    const isFrom = type === 'from';
    const fName = isFrom ? fromName : toName;
    const minD = isFrom ? minDate : fromVal ? fromVal : minDate || undefined;
    const maxD = isFrom ? (toVal ? toVal : maxDate) : maxDate;

    return (
      <div className="date-range-picker-range-block">
        <span className="date-range-picker-range-field-label">{isFrom ? 'From' : 'To'}</span>
        <Controller
          name={fName}
          control={control}
          defaultValue={null}
          render={({ field, fieldState: { error } }) => (
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <div className="date-range-picker-range-picker-wrap">
                <KeyboardDatePicker
                  {...commonPickerProps}
                  value={field.value ? new Date(field.value) : null}
                  minDate={minD}
                  maxDate={maxD}
                  onChange={(date) => handleDateChange(type, date, field)}
                  onBlur={field.onBlur}
                  error={!!error}
                  helperText={error?.message}
                />
              </div>
            </MuiPickersUtilsProvider>
          )}
        />
      </div>
    );
  };

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        const isInsidePicker = event.target.closest(
          '.MuiPopover-root, .MuiPickersPopper-root, .MuiDialog-root, [class*="MuiPickers"]'
        );
        if (!isInsidePicker) setOpen(false);
      }}>
      <div className="date-range-picker-wrapper" style={{ '--drp-width': width ? width : '180px' }}>
        {(isActive || open) && (
          <span className="date-range-picker-float-label">
            {label}
            {isRequired && <span className="date-range-picker-required-star">*</span>}
          </span>
        )}

        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          className={[
            'date-range-picker-trigger-btn',
            isActive ? 'active' : '',
            disabled ? 'disabled' : '',
          ].join(' ')}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={triggerLabel}>
          <span className="date-range-picker-trigger-left">
            <Typography className="date-range-picker-trigger-label">
              {triggerLabel}
              {!isActive && isRequired && (
                <span className="date-range-picker-required-star">*</span>
              )}
            </Typography>
          </span>

          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {isActive && (
              <RMSChip
                size="small"
                label="Clear"
                color="default"
                icon={<Close style={{ fontSize: 12 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  clearRange(e);
                }}
                className="date-range-picker-clear-chip"
              />
            )}
            <ExpandMore className={['date-range-picker-chevron', open ? 'open' : ''].join(' ')} />
          </span>
        </div>

        {open && (
          <Paper className="date-range-picker-dropdown" elevation={0}>
            <div className="date-range-picker-dropdown-body">
              <Typography className="date-range-picker-section-label">Quick Select</Typography>
              <div className="date-range-picker-presets-wrap">
                {allPresets.map((p) => (
                  <span
                    key={p.label}
                    className={[
                      'date-range-picker-preset-chip',
                      matchedPreset?.label === p.label ? 'selected' : '',
                    ].join(' ')}
                    onClick={() => applyPreset(p)}>
                    {p.label}
                  </span>
                ))}
              </div>

              <Divider className="date-range-picker-divider" />

              <Typography className="date-range-picker-section-label">Custom Range</Typography>
              {renderPicker('from')}
              {renderPicker('to')}
            </div>

            <div className="date-range-picker-dropdown-footer">
              {isActive && (
                <Button size="small" className="date-range-picker-footer-btn" onClick={clearRange}>
                  Clear dates
                </Button>
              )}
            </div>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default RMSDateRangePicker;
